import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * Loading component that displays a spinner with optional text.
 * Fixes scroll expansion issues by using proper Bootstrap 5 classes instead of vh-100/vw-100.
 *
 * @param {string} size - Size of the spinner: 'small' or 'normal' (default: 'normal')
 * @param {string} text - Text to display below the spinner (default: from i18n 'common.loading')
 * @param {string} className - Additional CSS classes to apply
 * @param {boolean} fullHeight - Whether to use full available height within layout (default: false)
 *
 * @example
 * // Basic usage
 * <Loading />
 *
 * @example
 * // Small spinner without text
 * <Loading size="small" text="" />
 *
 * @example
 * // Full height within layout (useful for page-level loading)
 * <Loading fullHeight={true} text="Loading page..." />
 */
function Loading({ size = 'normal', text, className = '', fullHeight = false }) {
  const { t } = useTranslation();
  const defaultText = text || t('common.loading');
  const spinnerSize = size === 'small' ? 'spinner-border-sm' : '';
  const containerClasses = `loading-container d-flex align-items-center justify-content-center ${
    fullHeight ? 'loading-full-height' : ''
  }`;

  return (
    <div className={`${containerClasses} ${className}`}>
      <div className="text-center">
        <div className={`spinner-border text-primary ${spinnerSize}`} role="status">
          <span className="visually-hidden">{defaultText}</span>
        </div>
        {defaultText && size !== 'small' && (
          <div className="mt-3 text-muted small">{defaultText}</div>
        )}
      </div>
    </div>
  );
}

Loading.propTypes = {
  size: PropTypes.oneOf(['small', 'normal']),
  text: PropTypes.string,
  className: PropTypes.string,
  fullHeight: PropTypes.bool,
};

export default Loading;
